"use client"

import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion"
import { useRef, type MouseEvent, type ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * Motion vocabulary for LocalAuthorityHero. Same vibe as
 * storytelling-stage-island but tuned for local-business/trades feel —
 * trust + warmth + responsiveness rather than industrial-futurist.
 *
 *   - LocalAuthorityMediaTilt: mouse-tilt 3D + scroll-linked subtle scale
 *     on the hero media tile. Says "premium polish" without being flashy.
 *   - LocalAuthorityTrustLift: hover lift on trust point cards. Each card
 *     responds individually to attention.
 *   - LocalAuthorityMagneticCta: magnetic-pull effect on the call-now CTA.
 *     Subtle but rewards intent ("I'm about to click").
 *
 * Each wrapper is opt-in. The static composition still renders; islands
 * just add motion when the user can see it.
 */

interface MediaTiltProps {
  children: ReactNode
  className?: string
}

export function LocalAuthorityMediaTilt({ children, className }: MediaTiltProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tileRef = useRef<HTMLDivElement>(null)

  // Subtle scroll-linked scale + drift on the media tile so the composition
  // stays alive even when the user pauses scrolling.
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start end", "end start"] })
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.0, 1.08])
  const drift = useTransform(scrollYProgress, [0, 1], [16, -16])

  // 3D mouse tilt — gentler than the storytelling card (4° max vs 6°) because
  // a full photograph reads as flat when tilted too aggressively.
  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const rotateX = useSpring(tiltX, { stiffness: 160, damping: 22, mass: 0.5 })
  const rotateY = useSpring(tiltY, { stiffness: 160, damping: 22, mass: 0.5 })

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const el = tileRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    tiltY.set(x * 8)
    tiltX.set(-y * 8)
  }
  function handleMouseLeave() {
    tiltX.set(0)
    tiltY.set(0)
  }

  return (
    <div ref={containerRef} style={{ perspective: 1400 }} className={cn("relative", className)}>
      <motion.div
        ref={tileRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ scale, y: drift, rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative h-full w-full"
      >
        {children}
      </motion.div>
    </div>
  )
}

export function LocalAuthorityTrustLift({ children }: { children: ReactNode }) {
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      className="h-full"
    >
      {children}
    </motion.div>
  )
}

export function LocalAuthorityMagneticCta({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 220, damping: 20, mass: 0.3 })
  const sy = useSpring(y, { stiffness: 220, damping: 20, mass: 0.3 })

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 14)
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 10)
  }
  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: sx, y: sy }}
      className={cn("inline-flex", className)}
    >
      {children}
    </motion.div>
  )
}

/**
 * Subtle pulsing ring around an element (great for the rating badge or an
 * emergency-CTA halo). Auto-loops, respects reduced motion.
 */
export function LocalAuthorityPulse({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("relative", className)}>
      <motion.span
        aria-hidden="true"
        className="absolute inset-0 rounded-full border-2 border-primary/40"
        animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }}
        transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
      />
      <div className="relative">{children}</div>
    </div>
  )
}
